import type { MenuDto } from '@/app/domain/dtos/master/menu.dto'

export interface NavSection {
  type: 'section'
  label: string
}

export interface NavLink {
  path: string
  label: string
  icon: string
}

export interface NavGroup {
  type: 'group'
  label: string
  icon: string
  expanded: boolean
  links: NavLink[]
}

export interface NavPage {
  type: 'link'
  path: string
  label: string
  icon: string
}

export interface NavAction {
  type: 'action'
  label: string
  icon: string
  action: () => void
}

export type NavEntry = NavSection | NavGroup | NavPage | NavAction

/** The sidebar's groups/links, in display order — the single source of truth for the (group
 *  label, link label) -> route mapping used both to render the sidebar (app-shell.component.ts
 *  appends its own Logout action to this) and to resolve a Menu name pair to a route for the
 *  post-login landing page (getDefaultLandingPath below). */
export const NAV_ENTRIES: NavEntry[] = [
  { type: 'section', label: 'Main' },
  {
    type: 'group',
    label: 'Dashboard',
    icon: 'bx bx-home-circle',
    expanded: true,
    links: [
      { path: 'dashboard/financial', label: 'Financial Dashboard', icon: 'bx bx-wallet' },
      { path: 'dashboard/sales', label: 'Sales Dashboard', icon: 'bx bx-cart' },
      { path: 'dashboard/analytical', label: 'Analytical Dashboard', icon: 'bx bx-line-chart' },
    ],
  },

  { type: 'section', label: 'Master Data' },
  {
    type: 'group',
    label: 'Master',
    icon: 'bx bx-store',
    expanded: false,
    links: [
      { path: 'master/customers', label: 'Customers', icon: 'bx bx-user' },
      { path: 'master/suppliers', label: 'Suppliers', icon: 'bx bx-store-alt' },
      { path: 'master/companies', label: 'Companies', icon: 'bx bx-buildings' },
      { path: 'master/taxes', label: 'Taxes', icon: 'bx bx-receipt' },
      { path: 'master/transaction-types', label: 'Types', icon: 'bx bx-shape-triangle' },
    ],
  },
  {
    type: 'group',
    label: 'Product',
    icon: 'bx bx-package',
    expanded: false,
    links: [
      { path: 'master/brands', label: 'Brands', icon: 'bx bx-purchase-tag' },
      { path: 'master/categories', label: 'Categories', icon: 'bx bx-category' },
      { path: 'master/sub-categories', label: 'Sub Categories', icon: 'bx bx-category-alt' },
      { path: 'master/units', label: 'Units', icon: 'bx bx-ruler' },
      { path: 'master/item-masters', label: 'Item', icon: 'bx bx-box' },
    ],
  },

  { type: 'section', label: 'Operations' },
  {
    type: 'group',
    label: 'Transactions',
    icon: 'bx bx-transfer',
    expanded: false,
    links: [
      { path: 'transactions/purchases', label: 'Purchases', icon: 'bx bx-cart-alt' },
      { path: 'transactions/purchase-orders', label: 'Purchase Order', icon: 'bx bx-cart-download' },
      { path: 'transactions/sales', label: 'Sales', icon: 'bx bx-cart' },
      { path: 'transactions/sales-orders', label: 'Sales Order', icon: 'bx bx-cart-add' },
      { path: 'transactions/sales-quotation', label: 'Sales Quotation', icon: 'bx bx-file' },
      { path: 'transactions/delivery-inward', label: 'Delivery Inward', icon: 'bx bx-import' },
      { path: 'transactions/delivery-outward', label: 'Delivery Outward', icon: 'bx bx-export' },
      { path: 'transactions/projects', label: 'Project BOM', icon: 'bx bx-list-check' },
    ],
  },
  {
    type: 'group',
    label: 'Shipment',
    icon: 'bx bx-package',
    expanded: false,
    links: [{ path: 'shipment/courier', label: 'Courier', icon: 'bx bx-package' }],
  },

  { type: 'section', label: 'Internal' },
  {
    type: 'group',
    label: 'Internal',
    icon: 'bx bx-lock-open-alt',
    expanded: false,
    links: [
      { path: 'transactions/internal-purchases', label: 'Purchases', icon: 'bx bx-cart-alt' },
      { path: 'transactions/internal-sales', label: 'Sales', icon: 'bx bx-cart' },
    ],
  },

  { type: 'section', label: 'Administration' },
  {
    type: 'group',
    label: 'User Management',
    icon: 'bx bx-group',
    expanded: false,
    links: [
      { path: 'user-management/employees', label: 'Employees', icon: 'bx bx-id-card' },
      { path: 'user-management/user-types', label: 'User Types', icon: 'bx bx-shield-quarter' },
      { path: 'master/menus', label: 'Menus', icon: 'bx bx-menu' },
      { path: 'user-management/menu-access', label: 'Menu Access', icon: 'bx bx-lock-alt' },
    ],
  },

  { type: 'section', label: 'Reports' },
  {
    type: 'group',
    label: 'Report',
    icon: 'bx bx-bar-chart-alt-2',
    expanded: false,
    links: [
      { path: 'report/sales', label: 'Sales Report', icon: 'bx bx-cart' },
      { path: 'report/purchase', label: 'Purchase Report', icon: 'bx bx-cart-alt' },
    ],
  },
]

/** A submenu is granted if its (parent name, own name) pair appears in the user's menu list — a
 *  bare name match isn't enough since several groups share submenu names (e.g. "Purchases" exists
 *  under both Transactions and Internal). */
export function getGrantedLinkKeys(menus: MenuDto[]): Set<string> {
  return new Set(
    menus.filter((menu) => menu.parentMenuId !== null).map((menu) => `${menu.parentMenuName}|${menu.name}`),
  )
}

/** (parent group name, submenu name) -> route, read off NAV_ENTRIES so this can never drift
 *  from what the sidebar itself renders. */
const pathByMenuKey = new Map<string, string>(
  NAV_ENTRIES.flatMap((entry) =>
    entry.type === 'group'
      ? entry.links.map((link): [string, string] => [`${entry.label}|${link.label}`, link.path])
      : [],
  ),
)

/**
 * The page a just-logged-in user should land on: their highest-priority granted submenu, ranked
 * by the Menu table's own DisplayOrder — the parent group's order first (so a granted Dashboard
 * variant always wins, since Dashboard is seeded as DisplayOrder 1), then the submenu's order
 * within that group. This is driven by whatever Menu Access/Menu admin screens have actually
 * configured for the user's UserType, not by this file's own listing order. Falls back to the
 * financial dashboard if the user somehow has no granted, routable submenu at all.
 */
export function getDefaultLandingPath(menus: MenuDto[]): string {
  const parentDisplayOrderByName = new Map(
    menus.filter((menu) => menu.parentMenuId === null).map((menu) => [menu.name, menu.displayOrder]),
  )

  const granted = menus
    .filter((menu) => menu.parentMenuId !== null)
    .map((menu) => ({
      path: pathByMenuKey.get(`${menu.parentMenuName}|${menu.name}`),
      parentOrder: parentDisplayOrderByName.get(menu.parentMenuName) ?? Number.MAX_SAFE_INTEGER,
      order: menu.displayOrder,
    }))
    .filter((entry): entry is { path: string; parentOrder: number; order: number } => entry.path !== undefined)
    .sort((a, b) => a.parentOrder - b.parentOrder || a.order - b.order)

  return granted[0]?.path ?? 'dashboard/financial'
}
